import { vec3, vec4 } from "gl-matrix";
import { Subject } from "rxjs";
import { AssetLoader } from "./AssetLoader/AssetLoader";
import { HttpBinaryAsset } from "./AssetLoader/HttpBinaryAsset";
import { FPSCounter } from "./Development/FPSCounter";
import { VisualOctree } from "./Development/VisualOctree";
import { VisualPathNodes } from "./Development/VisualPathNodes";
import { Collection } from "@spi/Common/Geometry";
import { InputHandler } from "./Input/InputHandler";
import { LightManager } from "./Light/LightManager";
import { PathNode } from "@spi/Common/NavigationMesh";
import { Mesh, GeometryMesh, PreCompiledGeometryMesh } from "./Object/Mesh";
import { NavigationAwareObject } from "./Object/NavigationAwareObject";
import { IDrawableObject, ObjectManager } from "./Object/ObjectManager";
import { RenderObject } from "./Object/RenderObject";
import { GateResolver } from "@spi/Common/PathFinding/";
import { NavigationMeshPathFinder } from "@spi/Common/NavigationMesh";
import { PickingManager } from "./Picking/PickingManager";
import { SceneStateReader } from "./Serialization/SceneStateReader";
import { TextElement } from "./Text/TextElement";
import { TextManager } from "./Text/TextManager";
import { TickEventManager, SpatialDatabase, BoundingBox } from "@spi/Common/Util";
import { ViewPort } from "./View/ViewPort";
import { Engine, Context } from "../Engine";
import { SceneState } from "./SceneState";
import { TextureAsset } from "./AssetLoader";

export class Scene {
    public callbacks: SceneCallbacks;

    public boundingBox: BoundingBox;
    public tickEventManager: TickEventManager;
    public lightManager: LightManager;
    public textManager: TextManager;
    public objectManager: ObjectManager;
    public pickingManager: PickingManager;

    public debugManager: VisualDebugManager;
    public gateResolver: GateResolver;
    public pathFinder: NavigationMeshPathFinder;
    public navigationGraph: PathNode[];
    public navigationDatabase: SpatialDatabase<PathNode>;

    public geometry: Map<string, RenderObject>;

    constructor(public engine: Engine, public viewPort: ViewPort, public inputHandler: InputHandler) {

        this.tickEventManager = new TickEventManager();
        this.lightManager = new LightManager(vec3.fromValues(0.50, 0.50, 0.50));
        this.textManager = new TextManager(engine.overlayLayer);
        this.objectManager = new ObjectManager();
        this.pickingManager = new PickingManager();

        this.callbacks = {};

        this.debugManager = new VisualDebugManager(this);
        this.setupSceneDebug();
    }

    public async downloadScene(uri: string): Promise<Scene> {
        let stateLoader = new AssetLoader();
        let state: SceneState = null;
        stateLoader.addAsset(new HttpBinaryAsset(uri, (e, buffer) => {
            state = new SceneStateReader().read(new Uint8Array(buffer));
        }));

        await stateLoader.resolveAssets(this.engine);

        let textureLoader = new AssetLoader();

        for(let texture of state.textureAssets) {
            textureLoader.addAsset(new TextureAsset(texture.reference, texture.uri, texture.translucent));
        }

        await textureLoader.resolveAssets(this.engine);

        this.load(state);
        return this;
    }

    private load(state: SceneState) {
        this.geometry = new Map<string, RenderObject>();

        for (let geo of state.geometryContainers) {
            let collection = new Collection();
            collection.addFaces(geo.faces);
            collection.calculateNormals();

            let mesh = new GeometryMesh(geo.reference, this.engine.ctx);
            mesh.bufferGeneratedMesh(collection, geo.textureReference ? 'pnt' : 'pnc');

            let obj = this.engine.createObject(mesh, (d) => {
                d.setScale(1);
                d.pickable = false;
                if (geo.textureReference) {
                    d.texture = this.engine.materialManager.getTexture(geo.textureReference);
                }
            });

            this.geometry.set(geo.reference, obj);
        }

        for(let cgeo of state.compiledGeometry) {
            let mesh = new PreCompiledGeometryMesh(cgeo.reference, this.engine.ctx);
            mesh.bufferMesh(cgeo.geometry.geometryData, cgeo.geometry.vertexLength, cgeo.geometry.vertexCount);

            let obj = this.engine.createObject(mesh, (d) => {
                d.setScale(1);
                d.pickable = false;
                if (cgeo.textureReference) {
                    d.texture = this.engine.materialManager.getTexture(cgeo.textureReference);
                }
            });

            this.geometry.set(cgeo.reference, obj);
        }

        this.boundingBox = state.boundingBox;
        this.navigationDatabase = new SpatialDatabase(this.boundingBox);
        this.navigationGraph = state.navigationGraph;
        this.navigationDatabase.addArray(state.navigationGraph);

        this.gateResolver = new GateResolver();
        this.pathFinder = new NavigationMeshPathFinder(this.gateResolver);
    }

    public createNavigationAwareObject(obj: { position: vec3 | number[] }, speed: number): NavigationAwareObject {
        // TODO: keep track of nav objects and dispose them when required
        let nav = new NavigationAwareObject(obj, this, speed);

        this.tickEventManager.registerFrameEvent(() => {
            nav.movement.step();
        });

        return nav;
    }

    public attached() {
        if (this.callbacks.onAttached) {
            this.callbacks.onAttached();
        }
        //this.uiManager.attach(this);
    }

    public detached() {
        if (this.callbacks.onDetached) {
            this.callbacks.onDetached();
        }
        //this.uiManager.detach(this);
    }

    public preRender(mode: string, ctx: Context) {
        if (this.callbacks.onPreRender) {
            this.callbacks.onPreRender(mode, ctx);
        }
    }

    private setupSceneDebug() {
        this.debugManager.add('navigation-graph', false).stateChanged.subscribe(dbg => {
            if (dbg.enabled) {
                let debugPathNodes = new VisualPathNodes(this.engine.ctx);
                debugPathNodes.setPathNodes(this.navigationGraph, (s) => vec4.fromValues(Math.random(), Math.random(), Math.random(), 0.8));
                dbg.objects = [debugPathNodes];
            }
        });

        this.debugManager.add('navigation-octree', false).stateChanged.subscribe(dbg => {
            if (dbg.enabled) {
                let debugTree = new VisualOctree(this.engine.ctx);
                debugTree.setTree(this.navigationDatabase.octree, false, true);
                dbg.objects = [debugTree];
            }
        });

        let fpsData: {
            label: TextElement,
            counter: FPSCounter
        } = null;

        this.debugManager.add('fps', false).stateChanged.subscribe(dbg => {
            if (dbg.enabled) {
                let fpsLabel = new TextElement(false, false);
                fpsLabel.position = vec3.fromValues(-600, 400, 0);
                this.textManager.addText(fpsLabel);

                fpsData = {
                    label: fpsLabel,
                    counter: new FPSCounter(this.engine, (counter) => fpsLabel.text = `${counter.fps} FPS`)
                };
            } else {
                if (fpsData) {
                    this.textManager.removeText(fpsData.label);
                    fpsData.counter.cancel();
                    fpsData = null;
                }
            }
        });
    }
}

// TODO: Refactor callback mechanism to use Rxjs
type SceneCallbacks = {
    onAttached?: () => void,
    onDetached?: () => void,
    onPreRender?: (mode: string, ctx: Context) => void
}

class VisualDebugManager {
    private debugVisualizers: Map<string, VisualDebug[]>;

    constructor(public scene: Scene) {
        this.debugVisualizers = new Map<string, VisualDebug[]>();
    }

    public add(key: string, enable?: boolean): VisualDebug {
        if (!this.debugVisualizers.has(key)) {
            this.debugVisualizers.set(key, []);
        }

        let debug = new VisualDebug(key, this.scene);

        this.debugVisualizers.get(key).push(debug);

        if (enable) {
            debug.enabled = true;
        }

        return debug;
    }

    public enable(key: string) {
        this.setEnabledState(key, true);
    }

    public disable(key: string) {
        this.setEnabledState(key, false);
    }

    public setEnabledState(key: string, enabled: boolean) {
        if (this.debugVisualizers.has(key)) {
            this.debugVisualizers.get(key).forEach(debug => debug.enabled = enabled);
        }
    }
}

class VisualDebug {

    public stateChanged: Subject<VisualDebug>;

    private _objects: IDrawableObject[];
    private _enabled: boolean;

    constructor(public key: string, public scene: Scene) {
        this._objects = [];
        this._enabled = false;

        this.stateChanged = new Subject<VisualDebug>();
    }

    public get objects(): IDrawableObject[] {
        return this._objects;
    }

    public set objects(objects: IDrawableObject[]) {
        this.removeObjects();
        this._objects = objects;
        if (this.enabled) {
            this.addObjects();
        }
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    public set enabled(val: boolean) {
        this._enabled = val;
        this.stateChanged.next(this);

        if (val) {
            this.addObjects();
        } else {
            this.removeObjects();
        }
    }

    private removeObjects() {
        for (let obj of this._objects) {
            this.scene.objectManager.removeObject(obj);
        }
    }

    private addObjects() {
        for (let obj of this._objects) {
            this.scene.objectManager.addObject(obj);
        }
    }
}